import { useState, useRef, useEffect } from 'react';
import { Card } from '@/components/ui/card';

/**
 * Spotlight Card Component
 * Card dengan efek spotlight yang mengikuti posisi mouse
 * Menggunakan radial gradient untuk membuat efek pencahayaan dinamis yang smooth
 */
export default function SpotlightCard({ 
  children, 
  className = '',
  spotlightColor = 'rgba(6, 182, 212, 0.1)', // cyan-500 with lower opacity for smoother effect
  ...props 
}) {
  const cardRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setMousePosition({ x, y });
    };

    const handleMouseEnter = () => {
      setIsHovering(true);
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseenter', handleMouseEnter);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseenter', handleMouseEnter);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div ref={cardRef} className="relative group overflow-hidden">
      {/* Spotlight overlay - Multiple layers for smoother gradient */}
      <div
        className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-500 group-hover:opacity-100 rounded-lg"
        style={{
          background: isHovering
            ? `
              radial-gradient(1000px circle at ${mousePosition.x}px ${mousePosition.y}px, 
                ${spotlightColor}, 
                transparent 50%),
              radial-gradient(800px circle at ${mousePosition.x}px ${mousePosition.y}px, 
                rgba(6, 182, 212, 0.05), 
                transparent 60%),
              radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, 
                rgba(6, 182, 212, 0.03), 
                transparent 70%)
            `
            : 'transparent',
        }}
      />
      
      {/* Additional soft glow layer */}
      <div
        className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-700 group-hover:opacity-100 rounded-lg blur-xl"
        style={{
          background: isHovering
            ? `radial-gradient(800px circle at ${mousePosition.x}px ${mousePosition.y}px, 
                rgba(6, 182, 212, 0.08), 
                transparent 60%)`
            : 'transparent',
        }}
      />
      
      {/* Card content */}
      <Card className={`relative ${className}`} {...props}>
        {children}
      </Card>
    </div>
  );
}
